// src/pages/RentasAbiertas.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 📋 RENTAS ACTIVAS - SISTEMA RENTACAR eFIT
// Gestión de contratos vigentes y proceso de devolución
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useState } from "react";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useFiscal } from '../hooks/useFiscal';

const descargarContratoPDF = async (rentaId, numero_contrato) => {
  try {
    const res = await api.get(`/rencar/rentas/${rentaId}/contrato`, { responseType: 'blob' });
    const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `contrato-${numero_contrato || 'RC-' + String(rentaId).padStart(4, '0')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo descargar el contrato PDF', confirmButtonColor: '#3b82f6' });
  }
};

const RentasAbiertas = () => {
  const fiscal = useFiscal();
  const [rentas, setRentas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("todas"); // todas | en_renta | en_uso
  const navigate = useNavigate();

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔄 CARGAR RENTAS
  // ═══════════════════════════════════════════════════════════════════════════
  const cargarRentas = async () => {
    try {
      setCargando(true);
      const res = await api.get("/rencar/rentas/abiertas");
      setRentas(res.data.rentas || []);
    } catch (error) {
      console.error("❌ Error cargando rentas:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las rentas activas",
        confirmButtonColor: "#3b82f6"
      });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarRentas();
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔍 FILTRAR RENTAS
  // ═══════════════════════════════════════════════════════════════════════════
  const rentasFiltradas = () => {
    if (filtroEstado === "todas") return rentas;
    return rentas.filter(r => r.estado === filtroEstado);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎨 FUNCIÓN AUXILIAR: BADGE DE ESTADO
  // ═══════════════════════════════════════════════════════════════════════════
  const getEstadoBadge = (estado) => {
    const badges = {
      abierta: { bg: '#fef3c7', color: '#92400e', text: '🟡 Sin Inspección', icon: '⏳' },
      en_renta: { bg: '#dbeafe', color: '#1e40af', text: '🔵 Lista para Cobro', icon: '💵' },
      en_uso: { bg: '#d1fae5', color: '#065f46', text: '🟢 En Uso', icon: '🚗' }
    };

    const badge = badges[estado] || badges.abierta;

    return (
      <span style={{
        padding: '6px 12px',
        background: badge.bg,
        color: badge.color,
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        {badge.text}
      </span>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 📊 FUNCIÓN AUXILIAR: VERIFICAR SI ESTÁ VENCIDA
  // ═══════════════════════════════════════════════════════════════════════════
  const estaVencida = (fechaRetorno) => {
    return new Date(fechaRetorno) < new Date();
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎯 ACCIONES
  // ═══════════════════════════════════════════════════════════════════════════
  const irAInspeccion = (renta) => {
    if (renta.estado === 'abierta') {
      navigate(`/rencar/inspeccion-salida/${renta.id}`);
    } else if (renta.estado === 'en_uso') {
      navigate(`/rencar/inspeccion-entrada/${renta.id}`);
    } else {
      Swal.fire({
        icon: 'info',
        title: '💵 Lista para Cobro en POS',
        html: `
          <p style="margin-bottom:12px">
            <strong>${renta.Vehiculo?.marca} ${renta.Vehiculo?.modelo}</strong>
            (${renta.Vehiculo?.placa})<br/>
            <span style="color:#64748b;font-size:13px">Cliente: ${renta.Cliente?.nombre}</span>
          </p>
          <p style="font-size:15px;color:#1e40af;font-weight:600">
            USD $${parseFloat(renta.total_pagar||0).toLocaleString('en-US',{minimumFractionDigits:2})}
            ${renta.total_pagar_dop ? `<br/><span style="font-size:12px;color:#6366f1">≈ RD$ ${parseFloat(renta.total_pagar_dop).toLocaleString('es-DO',{minimumFractionDigits:2})}</span>` : ''}
          </p>
          <p style="font-size:13px;color:#64748b;margin-top:8px">
            La inspección de salida está completada.<br/>Ve al POS para procesar el cobro.
          </p>
        `,
        showCancelButton: true,
        confirmButtonText: 'Ir al POS →',
        cancelButtonText: 'Cerrar',
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#94a3b8',
      }).then((result) => {
        if (result.isConfirmed) navigate('/pos');
      });
    }
  };

  const verDetalle = async (id) => {
    try {
      const res = await api.get(`/rencar/rentas/${id}`);
      const r = res.data.renta;

      Swal.fire({
        title: `Renta #${r.id}`,
        html: `
          <div style="text-align: left; padding: 10px;">
            <p><strong>Vehículo:</strong> ${r.Vehiculo?.marca} ${r.Vehiculo?.modelo} (${r.Vehiculo?.placa})</p>
            <p><strong>Cliente:</strong> ${r.Cliente?.nombre}</p>
            <p><strong>${fiscal.id_fiscal_empresa_label || 'RNC'}/${fiscal.id_fiscal_persona_label || 'Cédula'}:</strong> ${r.Cliente?.rnc_cedula}</p>
            <p><strong>Salida:</strong> ${new Date(r.fecha_salida).toLocaleDateString()}</p>
            <p><strong>Retorno:</strong> ${new Date(r.fecha_retorno_programada).toLocaleDateString()}</p>
            <p><strong>Días:</strong> ${r.dias}</p>
            <p><strong>Estado:</strong> ${r.estado}</p>
            <hr>
            <p style="font-size: 18px;"><strong>Total contrato:</strong> USD $${parseFloat(r.total_pagar).toLocaleString('en-US',{minimumFractionDigits:2})}</p>
            ${r.total_pagar_dop ? `<p style="font-size:13px;color:#6366f1">≈ RD$ ${parseFloat(r.total_pagar_dop).toLocaleString('es-DO',{minimumFractionDigits:2})} (tasa ${parseFloat(r.tasa_cambio||0).toFixed(2)})</p>` : ''}
            ${r.observaciones ? `<p><strong>Obs:</strong> ${r.observaciones}</p>` : ''}
          </div>
        `,
        confirmButtonColor: '#3b82f6'
      });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  if (cargando) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Cargando rentas activas...</p>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={styles.container}>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <div style={styles.pageTitle}>
          <span style={styles.pageTitleIcon}>📋</span>
          <div>
            <h1 style={styles.pageTitleText}>Rentas Activas</h1>
            <p style={styles.pageTitleSub}>
              Gestión de contratos vigentes • {rentasFiltradas().length} activas
            </p>
          </div>
        </div>
        <div style={styles.pageStats}>
          <div style={styles.statCount}>
            🚗 {rentas.length} Vehículos Rentados
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div style={styles.filterBar}>
        <button
          style={filtroEstado === "todas" ? styles.filterBtnActive : styles.filterBtn}
          onClick={() => setFiltroEstado("todas")}
        >
          📊 Todas ({rentas.length})
        </button>
        <button
          style={filtroEstado === "en_renta" ? styles.filterBtnActive : styles.filterBtn}
          onClick={() => setFiltroEstado("en_renta")}
        >
          💵 Para Cobro ({rentas.filter(r => r.estado === 'en_renta').length})
        </button>
        <button
          style={filtroEstado === "en_uso" ? styles.filterBtnActive : styles.filterBtn}
          onClick={() => setFiltroEstado("en_uso")}
        >
          🟢 En Uso ({rentas.filter(r => r.estado === 'en_uso').length})
        </button>
      </div>

      {/* Tabla */}
      <div style={styles.tableCard}>
        {rentasFiltradas().length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📭</div>
            <div style={styles.emptyText}>
              No hay rentas {filtroEstado === "todas" ? "activas" : filtroEstado}
            </div>
            <div style={styles.emptyHint}>
              Las rentas aparecerán aquí cuando se creen nuevos contratos
            </div>
          </div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Vehículo</th>
                  <th style={styles.th}>Cliente</th>
                  <th style={styles.th}>Salida</th>
                  <th style={styles.th}>Retorno</th>
                  <th style={styles.th}>Estado</th>
                  <th style={{...styles.th, textAlign: 'right'}}>Total</th>
                  <th style={{...styles.th, textAlign: 'right'}}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rentasFiltradas().map((renta) => {
                  const vencida = estaVencida(renta.fecha_retorno_programada);
                  
                  return (
                    <tr key={renta.id} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.idBadge}>#{renta.id}</span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.placaBadge}>{renta.Vehiculo?.placa}</div>
                        <div style={styles.vehiculoNombre}>
                          {renta.Vehiculo?.marca} {renta.Vehiculo?.modelo}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{fontWeight: '600', color: '#1e293b'}}>
                          {renta.Cliente?.nombre}
                        </div>
                        <div style={{fontSize: '12px', color: '#64748b'}}>
                          {renta.Cliente?.rnc_cedula}
                        </div>
                      </td>
                      <td style={styles.td}>
                        {new Date(renta.fecha_salida).toLocaleDateString("es-DO")}
                      </td>
                      <td style={styles.td}>
                        <span style={vencida ? styles.fechaVencida : {}}>
                          {new Date(renta.fecha_retorno_programada).toLocaleDateString("es-DO")}
                          {vencida && ' ⚠️'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {getEstadoBadge(renta.estado)}
                      </td>
                      <td style={{...styles.td, textAlign: 'right'}}>
                        <span style={styles.montoText}>
                          USD ${parseFloat(renta.total_pagar || 0).toLocaleString('en-US', {minimumFractionDigits:2})}
                        </span>
                      </td>
                      <td style={{...styles.td, textAlign: 'right'}}>
                        <div style={styles.btnGroup}>
                          <button
                            style={styles.btnInfo}
                            onClick={() => verDetalle(renta.id)}
                            title="Ver detalles"
                          >
                            👁️
                          </button>
                          <button
                            style={{ ...styles.btnInfo, color: '#3b82f6', borderColor: '#3b82f6' }}
                            onClick={() => descargarContratoPDF(renta.id, renta.numero_contrato)}
                            title="Descargar contrato PDF"
                          >
                            📄
                          </button>
                          <button
                            style={styles.btnAction}
                            onClick={() => irAInspeccion(renta)}
                          >
                            {renta.estado === 'abierta' ? '📸 Inspección' :
                             renta.estado === 'en_uso' ? '📥 Recibir' :
                             '💵 Cobrar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Animaciones */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS eFIT
// ═══════════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: "calc(100vh - 70px)",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
    padding: "24px 32px",
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
  },

  // Page Header
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px"
  },
  pageTitle: {
    display: "flex",
    alignItems: "center",
    gap: "16px"
  },
  pageTitleIcon: {
    fontSize: "36px",
    background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
    padding: "14px",
    borderRadius: "16px",
    border: "2px solid #93c5fd"
  },
  pageTitleText: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#1e293b",
    margin: 0
  },
  pageTitleSub: {
    fontSize: "14px",
    color: "#64748b",
    margin: "4px 0 0 0"
  },
  pageStats: {
    display: "flex",
    gap: "12px"
  },
  statCount: {
    padding: "10px 16px",
    background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
    color: "#065f46",
    borderRadius: "12px",
    fontWeight: "700",
    fontSize: "14px",
    border: "2px solid #6ee7b7"
  },

  // Filter Bar
  filterBar: {
    display: "flex",
    gap: "12px",
    marginBottom: "20px",
    flexWrap: "wrap"
  },
  filterBtn: {
    padding: "10px 20px",
    background: "white",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#64748b",
    cursor: "pointer",
    transition: "all 0.2s"
  },
  filterBtnActive: {
    padding: "10px 20px",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    border: "2px solid #3b82f6",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "600",
    color: "white",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)"
  },

  // Table Card
  tableCard: {
    background: "white",
    borderRadius: "20px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: "1px solid #e2e8f0",
    overflow: "hidden"
  },
  tableWrapper: {
    overflowX: "auto"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse"
  },
  th: {
    padding: "14px 16px",
    textAlign: "left",
    background: "#f8fafc",
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    borderBottom: "2px solid #e2e8f0"
  },
  tr: {
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.2s"
  },
  td: {
    padding: "14px 16px",
    fontSize: "14px",
    color: "#334155"
  },

  // Badges & Elements
  idBadge: {
    padding: "4px 8px",
    background: "#f1f5f9",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#475569"
  },
  placaBadge: {
    background: "#1e293b",
    color: "white",
    padding: "4px 8px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "700",
    display: "inline-block",
    marginBottom: "4px"
  },
  vehiculoNombre: {
    fontWeight: "600",
    color: "#1e293b",
    fontSize: "13px"
  },
  montoText: {
    color: "#10b981",
    fontWeight: "700",
    fontSize: "15px"
  },
  fechaVencida: {
    color: "#dc2626",
    fontWeight: "700"
  },

  // Buttons
  btnGroup: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end"
  },
  btnInfo: {
    background: "#f1f5f9",
    border: "none",
    padding: "8px 12px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    transition: "all 0.2s"
  },
  btnAction: {
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: "10px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(59, 130, 246, 0.3)",
    transition: "all 0.2s",
    fontSize: "13px",
    whiteSpace: "nowrap"
  },

  // Empty State
  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    color: "#64748b"
  },
  emptyIcon: {
    fontSize: "64px",
    marginBottom: "16px"
  },
  emptyText: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#334155",
    marginBottom: "8px"
  },
  emptyHint: {
    fontSize: "14px",
    color: "#94a3b8"
  },

  // Loading
  loadingContainer: {
    minHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #e2e8f0",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite"
  },
  loadingText: {
    marginTop: "16px",
    color: "#64748b",
    fontSize: "16px",
    fontWeight: "600"
  }
};

export default RentasAbiertas;